import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { ContentPane } from 'src/components/layout/content-pane'
import { Button } from 'src/components/button'
import { useUser } from 'src/services/user'
import { userApi } from 'src/services/user/api'

interface GoogleOAuthUrlResponse {
  auth_url: string
}

interface GoogleTokenRevokeResponse {
  message: string
  user_id: string
}

const GoogleIntegrationCard = () => {
  const { t } = useTranslation()
  const [isConnecting, setIsConnecting] = useState(false)
  const queryClient = useQueryClient()
  const { data: user } = useUser()

  // Check if user has Google token
  const isConnected = Boolean(user?.googleToken)

  // Get Google OAuth redirect URL
  const getGoogleAuthUrl = useMutation<GoogleOAuthUrlResponse, Error>({
    mutationFn: async (): Promise<GoogleOAuthUrlResponse> => {
      return await userApi.csc.getGoogleOAuthUrl()
    },
    onSuccess: (data) => {
      // Redirect to Google OAuth
      window.location.href = data.auth_url
    },
    onError: (error) => {
      console.error('Failed to get Google auth URL:', error)
      setIsConnecting(false)
    },
  })

  // Disconnect Google account
  const disconnectGoogle = useMutation<GoogleTokenRevokeResponse, Error>({
    mutationFn: async (): Promise<GoogleTokenRevokeResponse> => {
      return await userApi.csc.revokeGoogleOAuthToken()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
    onError: (error) => {
      console.error('Failed to disconnect Google account:', error)
    },
  })

  const handleConnect = () => {
    setIsConnecting(true)
    getGoogleAuthUrl.mutate()
  }

  const handleDisconnect = () => {
    if (confirm(t('integrations.disconnectConfirm'))) {
      disconnectGoogle.mutate()
    }
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            <svg className="h-10 w-10" viewBox="0 0 24 24">
              <path
                style={{ fill: 'var(--google-blue)' }}
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                style={{ fill: 'var(--google-green)' }}
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                style={{ fill: 'var(--google-yellow)' }}
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                style={{ fill: 'var(--google-red)' }}
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-medium text-text-primary">
              {t('integrations.googleAccount')}
            </h3>
            <p className="text-sm text-text-placeholder">
              {isConnected
                ? t('integrations.googleConnected')
                : t('integrations.googleDisconnected')}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          {isConnected ? (
            <Button
              variant="secondary"
              onClick={handleDisconnect}
              disabled={disconnectGoogle.isPending}
            >
              {disconnectGoogle.isPending
                ? t('integrations.disconnecting')
                : t('integrations.disconnect')}
            </Button>
          ) : (
            <Button onClick={handleConnect} disabled={isConnecting || getGoogleAuthUrl.isPending}>
              {isConnecting || getGoogleAuthUrl.isPending
                ? t('integrations.connecting')
                : t('integrations.connectGoogle')}
            </Button>
          )}
        </div>
      </div>
      {isConnected && (
        <div className="mt-4 border-t border-border pt-4">
          <div className="flex items-center text-sm text-green-600">
            <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            {t('integrations.connectedSuccess')}
          </div>
        </div>
      )}
    </div>
  )
}

export const Integrations = () => {
  const { t } = useTranslation()
  return (
    <>
      <PageHeader title={t('integrations.title')} />
      <MainContent>
        <ContentPane>
          <div className="space-y-6">
            <div>
              <div className="space-y-4">
                <GoogleIntegrationCard />

                {/* Placeholder for future integrations */}
                <div className="rounded-lg border border-border bg-surface p-6 opacity-50 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-300">
                          <span className="text-sm text-text-secondary">?</span>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-text-primary">
                          {t('integrations.moreIntegrations')}
                        </h3>
                        <p className="text-sm text-text-placeholder">
                          {t('integrations.comingSoon')}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <Button variant="secondary" disabled>
                        {t('integrations.comingSoonBtn')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContentPane>
      </MainContent>
    </>
  )
}
